import { Card, CardContent } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  CaretDown,
  CheckCircle,
  Warning,
  WarningCircle,
  Info,
} from '@phosphor-icons/react';
import type { RemittanceDetail } from '@erp/rpc';
import { T } from 'gt-react';

type VerificationResult = NonNullable<RemittanceDetail['verificationResult']>;
type Finding = VerificationResult['findings'][number];

interface RemittanceVerificationPanelProps {
  verificationResult: RemittanceDetail['verificationResult'];
}

const SEVERITY_STYLES: Record<
  Finding['severity'],
  { icon: typeof Info; className: string }
> = {
  critical: {
    icon: WarningCircle,
    className: 'border-red-200 bg-red-50 text-red-800',
  },
  warning: {
    icon: Warning,
    className: 'border-amber-200 bg-amber-50 text-amber-800',
  },
  info: {
    icon: Info,
    className: 'border-slate-200 bg-slate-50 text-slate-700',
  },
};

function FindingRow({ finding }: { finding: Finding }) {
  const style = SEVERITY_STYLES[finding.severity];
  const Icon = style.icon;
  return (
    <div
      className={`flex gap-2 rounded-md border p-2 text-xs ${style.className}`}
    >
      <Icon className="mt-0.5 shrink-0" size={16} weight="fill" />
      <div className="space-y-0.5">
        <div className="font-medium">{finding.title}</div>
        <div className="opacity-90">{finding.detail}</div>
        {finding.reference && (
          <div className="font-mono text-[11px] opacity-70">
            <T context="ar remittance verification finding reference">Ref</T>:{' '}
            {finding.reference}
          </div>
        )}
      </div>
    </div>
  );
}

type Verdict = VerificationResult['verdict'];

const VERDICT_TEXT_CLASSES: Record<Verdict, string> = {
  pass: 'text-green-700',
  needs_review: 'text-amber-700',
};

/** The verdict as plain coloured text with its icon — no pill. */
function VerdictBadge({ verdict }: { verdict: Verdict }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${VERDICT_TEXT_CLASSES[verdict]}`}
    >
      {verdict === 'pass' ? (
        <>
          <CheckCircle size={14} weight="fill" />
          <T context="ar remittance verification verdict pass">Verified</T>
        </>
      ) : (
        <>
          <Warning size={14} weight="fill" />
          <T context="ar remittance verification verdict needs review">
            Review
          </T>
        </>
      )}
    </span>
  );
}

function VerificationDetails({ result }: { result: VerificationResult }) {
  return (
    <>
      <p className="text-[11px] text-muted-foreground">
        <T context="ar remittance verification scope caption">
          Original extraction checked against the source document at ingest —
          not re-run after edits.
        </T>
      </p>
      <div className="text-sm text-muted-foreground">{result.summary}</div>
      {result.findings.length > 0 && (
        <div className="space-y-1.5">
          {result.findings.map((finding, i) => (
            <FindingRow
              key={`${finding.reference ?? finding.title}-${i}`}
              finding={finding}
            />
          ))}
        </div>
      )}
      <div className="text-[11px] text-muted-foreground">
        <T context="ar remittance verification checked timestamp">Checked</T>{' '}
        {new Date(result.verifiedAt).toLocaleString()}
      </div>
    </>
  );
}

/**
 * Surfaces the AI overseer's check of the extracted remittance against its
 * source document (persisted by the ingestion workflow). Collapsed to a single
 * line — title and verdict — with the summary and findings on click. Renders
 * nothing for remittances that were never checked.
 */
export function RemittanceVerificationPanel({
  verificationResult,
}: RemittanceVerificationPanelProps) {
  if (!verificationResult) return null;

  return (
    <Card className="gap-0 p-0">
      <Collapsible>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="group flex w-full items-center justify-between gap-2 p-3 text-left hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-inset"
          >
            <span className="text-base font-semibold">
              <T context="ar remittance verification panel title">
                Verification
              </T>
            </span>
            <span className="flex items-center gap-2">
              <VerdictBadge verdict={verificationResult.verdict} />
              <CaretDown
                className="size-4 text-muted-foreground transition-transform group-data-[panel-open]:rotate-180"
                aria-hidden
              />
            </span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-2 px-3 pb-3">
            <VerificationDetails result={verificationResult} />
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
